import { Layout } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faMinus,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons'
import { matchPath, useLocation, useHistory } from 'react-router'
import Cookies from 'js-cookie'
import * as fromGeneralActions from 'store/actions/general.action'
import { useIsMobile } from 'hooks/useIsMobile'
import { getHomeConfiguration } from 'api/enterprise'
import rankmiFallback from 'assets/rankmi.png'
import Img from 'components/ui/Img'
import styles from './NewHome.module.scss'
import SidebarMenu from './components/sidebar/SidebarMenu'
import NavBar from './components/navbar/NavBar'
import { MobileMenu } from './components/mobile-menu/MobileMenu'
import { useMenuItems } from './components/useMenuItems'
import { SubMenu, SubMenuItem, SubMenuContainer, ScrollButton } from './styled'

const { Header, Sider, Content } = Layout

const NewHome: React.FC = ({ children }) => {
  const dispatch = useDispatch()
  const isAppMobile = Cookies.getJSON('rankmi_flavour_mobile')
  const [siderIsCollapsed, setSiderIsCollapsed] = useState(false)
  const subMenuRef = useRef<HTMLDivElement>(null)
  const { activeMenuItem, activeSubMenuItem, menuItems, showSubMenu } =
    useMenuItems()
  const history = useHistory()
  const [logoSrc, setLogoSrc] = useState(rankmiFallback)
  const location = useLocation()

  useEffect(() => {
    dispatch(fromGeneralActions.Actions.get())
    getHomeConfiguration()
      .then((response) => {
        const logo = response.data?.homeLogo ?? response.data?.home_logo
        if (logo) {
          setLogoSrc(logo)
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [dispatch])

  useEffect(() => {
    if (
      matchPath(location.pathname, {
        path: [
          '/courses/:courseId',
          '/administration/courses/:courseId/preview',
        ],
      })
    ) {
      setSiderIsCollapsed(true)
    }
  }, [location])

  const isMobile = useIsMobile()

  const scrollLeft = () => {
    if (subMenuRef.current) {
      subMenuRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (subMenuRef.current) {
      subMenuRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  return (
    <Layout>
      {!isMobile && (
        <Sider
          className={`sider ${
            siderIsCollapsed ? styles.siderCollapsed : styles.siderExtended
          } ${isMobile && !siderIsCollapsed ? styles.siderMobile : ''}`}
          collapsible={true}
          collapsedWidth={0}
          trigger={null}
        >
          <div className={styles.siderContent}>
            <div className={styles.siderHeader}>
              <div
                className={
                  styles.collapseSiderButton +
                  ' ' +
                  (siderIsCollapsed ? styles.collapsed : '')
                }
                onClick={() => setSiderIsCollapsed(!siderIsCollapsed)}
              >
                <FontAwesomeIcon
                  className={styles.collapseButtonIcon}
                  icon={siderIsCollapsed ? faPlus : faMinus}
                />
              </div>

              <div className={styles.companyContainer}>
                <div
                  className={
                    styles.rectangle +
                    ' ' +
                    (siderIsCollapsed ? styles.rectangleCollapsed : '')
                  }
                />
                {!siderIsCollapsed && (
                  <div className={styles.logoContainer}>
                    <Img
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_APP_URL}/#/v2/home`,
                          '_self'
                        )
                      }
                      className={styles.logo}
                      src={logoSrc}
                      alt="Logo"
                      imageHandlerProps={{ height: 50 }}
                      onError={() => setLogoSrc(rankmiFallback)}
                    />
                  </div>
                )}
              </div>
            </div>
            <SidebarMenu siderIsCollapsed={siderIsCollapsed} />
          </div>
        </Sider>
      )}
      <Layout>
        <Header
          className={`${styles.header} ${isMobile ? styles.navMobile : ''}`}
        >
          {!isAppMobile && <NavBar isMobile={isMobile} />}
          {!!isMobile && <MobileMenu />}
        </Header>
        {showSubMenu && (
          <SubMenu>
            {isMobile && (
              <ScrollButton className="left" onClick={scrollLeft}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </ScrollButton>
            )}
            <SubMenuContainer ref={subMenuRef} isMobile={isMobile}>
              {menuItems[activeMenuItem].subItems?.map((item, index) => (
                <SubMenuItem
                  key={index}
                  active={activeSubMenuItem === index}
                  onClick={() => item.url && history.push(item.url)}
                >
                  {item.text}
                </SubMenuItem>
              ))}
            </SubMenuContainer>
            {isMobile && (
              <ScrollButton className="right" onClick={scrollRight}>
                <FontAwesomeIcon icon={faChevronRight} />
              </ScrollButton>
            )}
          </SubMenu>
        )}
        <Content
          className={`${styles.content} ${
            isMobile ? styles.contentMobile : ''
          } ${
            isAppMobile
              ? showSubMenu
                ? styles.contentAppMobileWithSubmenu
                : styles.contentAppMobile
              : ''
          }`}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default NewHome
